import React, { useEffect, useState } from 'react';
import { LockKeyhole, Menu, Moon, Sun, X } from 'lucide-react';
interface NavbarProps {
  isPortfolioUnlocked: boolean;
  onRequestPortfolioAccess: () => void;
}
export function Navbar({
  isPortfolioUnlocked,
  onRequestPortfolioAccess
}: NavbarProps) {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isDarkMode, setIsDarkMode] = useState(false);
  useEffect(() => {
    if (document.documentElement.classList.contains('dark')) {
      setIsDarkMode(true);
    }
  }, []);
  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  const toggleTheme = () => {
    setIsDarkMode(currentTheme => {
      document.documentElement.classList.toggle('dark', !currentTheme);
      return !currentTheme;
    });
  };
  const navLinks = [{
    name: 'Studio',
    href: '#studio'
  }, {
    name: 'Capabilities',
    href: '#services'
  }, {
    name: 'Selected work',
    href: '#selected-work'
  }, {
    name: 'Client stories',
    href: '#testimonials'
  }, {
    name: 'Contact',
    href: '#contact'
  }];
  const requestAccess = () => {
    setIsMobileMenuOpen(false);
    onRequestPortfolioAccess();
  };
  const navClassName = `fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${isScrolled ? 'bg-dark/95 py-4 shadow-lg backdrop-blur-sm' : 'bg-transparent py-6'}`;
  return <nav className={navClassName} aria-label="Primary navigation" data-id="element-127">
      <div className="mx-auto flex max-w-[1320px] items-center justify-between px-4" data-id="element-128">
        <a href="#home" className="flex items-center" aria-label="Byteweb home" data-id="element-129">
          <img src="https://cdn.magicpatterns.com/uploads/5NTyxseHUpDeTNPmLZNFKL/ByteWeb8.png" alt="Byteweb" className="h-10 w-auto" data-id="element-130" />
        </a>

        <div className="hidden items-center gap-7 md:flex" data-id="element-131">
          {navLinks.map(link => <a key={link.name} href={link.href} className="font-inter text-sm font-medium uppercase tracking-wider text-white/80 transition-colors hover:text-gold focus:outline-none focus:ring-2 focus:ring-gold" data-id="element-132">
              {link.name}
            </a>)}
          {isPortfolioUnlocked ? <div className="flex items-center gap-5" data-id="element-133">
              <a href="#private-profile" className="font-inter text-sm font-semibold uppercase tracking-wider text-gold transition-colors hover:text-white focus:outline-none focus:ring-2 focus:ring-gold" data-id="element-134">
                Private profile
              </a>
              <a href="#portfolio" className="font-inter text-sm font-semibold uppercase tracking-wider text-gold transition-colors hover:text-white focus:outline-none focus:ring-2 focus:ring-gold" data-id="element-135">
                Work archive
              </a>
            </div> : <button type="button" onClick={requestAccess} className="inline-flex items-center gap-2 rounded-full border border-gold/50 px-4 py-2 font-inter text-xs font-bold uppercase tracking-wider text-gold transition-colors hover:bg-gold hover:text-true-white focus:outline-none focus:ring-2 focus:ring-gold" data-id="element-136">
              <LockKeyhole size={14} data-id="element-137" />
              Client access
            </button>}
        </div>

        <div className="flex items-center gap-3" data-id="element-138">
          <button type="button" onClick={toggleTheme} className="rounded-full border border-white/10 bg-white/5 p-2 text-white transition-colors hover:text-gold focus:outline-none focus:ring-2 focus:ring-gold" aria-label="Toggle theme" data-id="element-139">
            {isDarkMode ? <Sun size={19} data-id="element-140" /> : <Moon size={19} data-id="element-141" />}
          </button>
          <button type="button" className="text-white transition-colors hover:text-gold focus:outline-none focus:ring-2 focus:ring-gold md:hidden" onClick={() => setIsMobileMenuOpen(isOpen => !isOpen)} aria-label={isMobileMenuOpen ? 'Close menu' : 'Open menu'} aria-expanded={isMobileMenuOpen} data-id="element-142">
            {isMobileMenuOpen ? <X size={24} data-id="element-143" /> : <Menu size={24} data-id="element-144" />}
          </button>
        </div>
      </div>

      {isMobileMenuOpen && <div className="absolute left-0 right-0 top-full border-t border-white/10 bg-dark p-5 shadow-xl md:hidden" data-id="element-145">
          <div className="flex flex-col gap-2" data-id="element-146">
            {navLinks.map(link => <a key={link.name} href={link.href} className="block rounded-lg px-3 py-2.5 font-inter text-base font-medium text-white/80 transition-colors hover:bg-white/5 hover:text-gold" onClick={() => setIsMobileMenuOpen(false)} data-id="element-147">
                {link.name}
              </a>)}
            {isPortfolioUnlocked ? <div className="mt-2 flex flex-col gap-1" data-id="element-148">
                <a href="#private-profile" className="rounded-lg px-3 py-2.5 font-inter text-base font-semibold text-gold" onClick={() => setIsMobileMenuOpen(false)} data-id="element-149">
                  Private profile
                </a>
                <a href="#portfolio" className="rounded-lg px-3 py-2.5 font-inter text-base font-semibold text-gold" onClick={() => setIsMobileMenuOpen(false)} data-id="element-150">
                  Work archive
                </a>
              </div> : <button type="button" onClick={requestAccess} className="mt-2 inline-flex items-center justify-center gap-2 rounded-lg bg-gold px-4 py-3 font-inter text-sm font-bold text-true-white" data-id="element-151">
                <LockKeyhole size={16} data-id="element-152" />
                Client access
              </button>}
          </div>
        </div>}
    </nav>;
}